import { VStack, HStack, Box, WarningIcon } from 'native-base';
import { Caption, H2 } from '~/shared/Typography';
import { Plan, useGetMember, useI18n, useTheme } from '@springbig/shared';
import React, { useEffect, useState } from 'react';
import { useAppNavigate } from '~/pages';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import EmailVerificationForm from '~/shared/EmailVerification/EmailVerificationForm';
import { useToast } from '~/hooks/toast';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import CreditCardForm from '~/shared/CreditCardForm/CreditCardForm';
import CancelSubscription from '~/pages/subscriptions/components/CancelSubscription';
import UseManageFlow from '~/pages/subscriptions/hooks/useManageFlow';
import { format } from 'date-fns';
import { Button, Panel } from '~/shared/Theme';

type ManageFlowProps = {
    plan: Plan;
};

type Pages = 'update email' | 'change card' | 'cancel subscription';

type ManageOption = {
    page: Pages;
    title?: string;
    icon?: IconDefinition;
    isDisabled?: boolean;
    hasWarning?: boolean;
};

const ManageFlow = ({ plan }: ManageFlowProps) => {

    const { navigate, params } = useAppNavigate();

    const { primary, radius_lg } = useTheme();

    const { successToast } = useToast();
    const { t } = useI18n();
    const { refetch: refreshMember } = useGetMember();

    const [isInSubPage, setIsInSubPage] =
        useState<boolean>(false);

    const [isUpdateEmailScreen, setIsUpdateEmailScreen] =
        useState<boolean>(false);

    const [isChangeCardScreen, setIsChangeCardScreen] =
        useState<boolean>(false);

    const [isCancelSubscriptionScreen, setIsCancelSubscriptionScreen] =
        useState<boolean>(false);

    useEffect(() => {

        if (isUpdateEmailScreen || isChangeCardScreen || isCancelSubscriptionScreen) {
            setIsInSubPage(true);
        }
        else {
            setIsInSubPage(false);
        }
    }, [isInSubPage, isUpdateEmailScreen, isChangeCardScreen, isCancelSubscriptionScreen]);

    const onCancel = (): void => {
        navigate(`w/${params.wallet_id}/subscriptions`);
    };

    const onChangePage = (page: Pages): void => {

        switch (page) {
        case 'update email':
            setIsUpdateEmailScreen(true);
            break;
        case 'change card':
            setIsChangeCardScreen(true);
            break;
        case 'cancel subscription':
            setIsCancelSubscriptionScreen(true);
            break;
        default:
            console.error(`ManageFlow > ${page} does not exist`);
            break;
        }
    };

    const onSuccessfulEmailUpdate = (): void => {

        setIsUpdateEmailScreen(false);
        successToast('email successfully updated');
    };

    const onSuccessfulCardUpdate = (): void => {

        setIsChangeCardScreen(false);
        successToast('card successfully updated');
    };

    const onSuccessfulCancellation = (): void => {

        setIsCancelSubscriptionScreen(false);
        refreshMember();
        successToast('subscription successfully cancelled');
    };

    const {
        apiError,
        isLoadingUpdateCard,
        isLoadingCancelSubscription,
        onSubmitCancelSubscription,
        onSubmitUpdateCreditCard,
    } = UseManageFlow({
        plan,
        onSuccessfulCardUpdate,
        onSuccessfulCancellation
    });

    const generateActiveUntilDate = (terminatedAt: Date): string => {

        return t('subscriptions.active_until', [format(new Date(terminatedAt), 'PPP')]);
    };

    const manageOptions: Array<ManageOption> = [
        {
            page: 'update email',
            title: t('subscriptions.manage.update_email'),
            icon: faChevronRight,
        },
        {
            page: 'change card',
            title: t('subscriptions.manage.change_card'),
            icon: faChevronRight,
            hasWarning: plan.subscription?.status === 'suspended',
        },
        {
            page: 'cancel subscription',
            icon: plan.subscription?.status !== 'active' ? undefined : faChevronRight,
            title: plan.subscription?.status !== 'active' ? generateActiveUntilDate(plan.subscription.terminated_at) : t('subscriptions.manage.cancel_subscription'),
            isDisabled: plan.subscription?.status !== 'active',
        },
    ];

    return (
        !isInSubPage ?
            <VStack>
                <VStack
                    minH={radius_lg}
                    alignItems={'center'}
                    justifyContent={'center'}
                    w={'full'}
                >
                    <H2
                        textAlign={'center'}
                        text={'subscriptions.manage.title'}
                        textValues={[plan.name]}
                    />
                </VStack>

                <Panel space={8}>
                    { manageOptions.map((option) => {

                        return (
                            <Button
                                justifyContent={option.icon ? 'space-between' : 'center'}
                                opacity={option.isDisabled ? .5 : 1}
                                key={option.title}
                                modifier={'unlocked'}
                                variant={'text'}
                                disabled={option.isDisabled}
                                onPress={() => onChangePage(option.page)}
                            >
                                <HStack justifyContent={'space-between'} alignItems={'center'}>
                                    <Caption>
                                        { option.title || option.page }
                                    </Caption>
                                    <HStack space={2}>
                                        { option.hasWarning &&
                                            <WarningIcon size={5} color={'red'} />
                                        }
                                        { option.icon &&
                                            <SpIcon
                                                color={primary}
                                                icon={option.icon}
                                            />
                                        }
                                    </HStack>
                                </HStack>
                            </Button>
                        );
                    })}
                </Panel>
                <Button
                    m={4}
                    text={'subscriptions.back_button'}
                    onPress={onCancel}
                />
            </VStack>
            :
            isUpdateEmailScreen ? (
                <Box px={4}>
                    <VStack
                        minH={radius_lg}
                        alignItems={'center'}
                        justifyContent={'center'}
                        w={'full'}
                    >
                        <H2
                            textAlign={'center'}
                            text={'subscriptions.update_email_info'}
                        />
                    </VStack>
                    <EmailVerificationForm
                        t={t}
                        isRequiredTerms
                        walletId={params.wallet_id}
                        onCancel={() => setIsUpdateEmailScreen(false)}
                        onProceed={onSuccessfulEmailUpdate}
                    />
                </Box>
            ) : isChangeCardScreen ? (
                <Box px={4}>
                    <VStack
                        minH={radius_lg}
                        alignItems={'center'}
                        justifyContent={'center'}
                        w={'full'}
                    >
                        <H2
                            textAlign={'center'}
                            text={'credit_card.add_card'}
                        />
                    </VStack>
                    <CreditCardForm
                        onCancel={() => setIsChangeCardScreen(false)}
                        onProceed={(data) => onSubmitUpdateCreditCard(data)}
                        isLoading={isLoadingUpdateCard}
                        error={apiError}
                    />
                </Box>
            ) : (
                <Box p={4}>
                    <CancelSubscription
                        onCancel={() => setIsCancelSubscriptionScreen(false)}
                        onProceed={onSubmitCancelSubscription}
                        isLoading={isLoadingCancelSubscription}
                        error={apiError}
                    />
                </Box>
            )
    );
};

export default ManageFlow;
